"use client";

import { useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { ChartNavigationSlider } from "@/modules/stochastic/features/DashboardPage/components/ChartNavigationSlider/ChartNavigationSlider";
import { formatCurrency, generateColors } from "../../../utils/chart";
import { ChartMetricCard } from "@/modules/stochastic/features/DashboardPage/components/ChartMetricCard/ChartMetricCard";
import { TotalSalesNewCustomerByZipCodeAndYearData } from "@/modules/stochastic/features/DashboardPage/api/fetchStochasticDashboardData/types";
import { ChartBarLegendWithColoredYear } from "@/modules/stochastic/features/DashboardPage/components/ChartBarLegendWithColoredYear/ChartBarLegendWithColoredYear";
import ChartTooltipWithColoredYears from "@/modules/stochastic/features/DashboardPage/components/ChartTooltipWithColoredYears/ChartTooltipWithColoredYears";
import { useChartPagination } from "@/modules/stochastic/features/DashboardPage/hooks/useChartPagination";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface TotalSalesNewCustomerByZipCodeAndYearChartProps {
  chartId?: string;
  initialData: TotalSalesNewCustomerByZipCodeAndYearData;
}

export default function TotalSalesNewCustomerByZipCodeAndYearChart({
  initialData,
}: TotalSalesNewCustomerByZipCodeAndYearChartProps) {
  if (!initialData?.items || initialData.items.length === 0) return null;

  const { theme } = useTheme();
  const { years, postalCodes, items } = initialData;
  const colors = generateColors(years, theme);

  // Sort postal codes by most recent year's sales value (descending)
  const sortedPostalCodes = useMemo(() => {
    const mostRecentYear = years[years.length - 1];

    // Create a map of postal code to sales for the most recent year
    const salesByPostalCode = new Map<string, number>();
    items.forEach(({ postalCode, year, sales }) => {
      if (year === mostRecentYear) {
        salesByPostalCode.set(postalCode, sales);
      }
    });

    // Sort postal codes by sales value (highest to lowest)
    return [...postalCodes].sort((a, b) => {
      const salesA = salesByPostalCode.get(a) || 0;
      const salesB = salesByPostalCode.get(b) || 0;
      return salesB - salesA;
    });
  }, [postalCodes, years, items]);

  const {
    page,
    setPage,
    totalPages,
    currentPageItems: currentPostalCodes,
  } = useChartPagination(sortedPostalCodes, 10);

  const chartData = useMemo(() => {
    return currentPostalCodes.map((zip) => {
      const entry: Record<string, number | string> = { postalCode: zip };
      items.forEach(({ postalCode, year, sales }) => {
        if (postalCode === zip) {
          entry[year] = sales;
        }
      });
      return entry;
    });
  }, [items, currentPostalCodes]);

  return (
    <Card className="w-full bg-white dark:bg-secondary-dark">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-fontColor dark:text-light">
            Total Sales New Customer by ZIP Code & Year
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                <p>
                  Shows how market capture is expanding or contracting in
                  specific neighborhoods.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          New customer revenue by ZIP codes over time
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer height={500} width="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 45, bottom: 5 }}
          >
            <CartesianGrid
              stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="postalCode"
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
            />
            <YAxis
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
              tickFormatter={formatCurrency}
            />
            <ChartTooltip
              content={
                <ChartTooltipWithColoredYears
                  colors={colors}
                  labelKey="postalCode"
                  labelPrefix="ZIP:"
                  valueSuffix=""
                />
              }
            />
            <Legend content={<ChartBarLegendWithColoredYear />} />
            {years.map((year) => (
              <Bar
                dataKey={year}
                fill={colors[year]}
                isAnimationActive={true}
                key={year}
                name={year}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>

        <div className="mt-8">
          <div className="text-sm text-gray-500 mb-3 text-center select-none font-semibold tracking-wide">
            Navigation Overview
          </div>

          <ChartNavigationSlider
            currentPage={page}
            itemsPerPage={10}
            onPageChange={setPage}
            postalCodes={sortedPostalCodes}
            theme={theme}
            totalPages={totalPages}
          />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-6 mt-6">
            <ChartMetricCard
              label="Current View"
              value={`${page + 1} of ${totalPages}`}
            />
            <ChartMetricCard label="ZIPs Shown" value={10} />
            <ChartMetricCard
              label="Total ZIPs"
              value={sortedPostalCodes.length}
            />
            <ChartMetricCard
              label="Years Tracked"
              value={`${years[0]}–${years.at(-1)}`}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
